import { type ComponentProps } from 'react'

import { FilterSubmitButton, useFilterFormError } from '@/components/common/Filter'
import { InputSelect, InputTypeahead } from '@/components/common/Input'
import {
  type CardFilterSelectPartialType,
  type CardFilterSelectSlugType,
  type CardFilterSelectType,
} from '@/defs/cardFilters'
import { SET_OPERATOR_LABELS, SET_OPERATORS } from '@/defs/common'
import { type TypeaheadSelectionType } from '@/defs/input'
import { RARITIES, RARITY_LABELS } from '@/defs/mtg'

import styles from './GalleryCardFiltersFormSelect.module.scss'

const CONFIG = {
  rarity: {
    'aria-label': 'Search rarities',
    options: RARITIES.map((r) => ({
      label: RARITY_LABELS[r].label,
      value: r,
    })),
    placeholder: 'Search rarities...',
  },
  sets: {
    'aria-label': 'Search sets',
    mode: 'set',
    placeholder: 'Search sets...',
  },
  tags: {
    'aria-label': 'Search tags',
    options: [
      { label: 'Card Draw', value: 'draw' },
      { label: 'Protection', value: 'protection' },
      { label: 'Ramp', value: 'ramp' },
      { label: 'Recursion', value: 'recursion' },
      { label: 'Removal', value: 'removal' },
      { label: 'Board Wipe', value: 'sweeper' },
      { label: 'Tutors', value: 'tutor' },
    ],
    placeholder: 'Search tags...',
  },
} satisfies Record<CardFilterSelectSlugType, Partial<ComponentProps<typeof InputTypeahead>>>

const OPTIONS = SET_OPERATORS.map((c) => ({
  label: SET_OPERATOR_LABELS[c].label,
  value: c,
}))

type Props = {
  cardFilter: CardFilterSelectPartialType
  onUpdate: (f: CardFilterSelectPartialType) => void
}

export const GalleryCardFiltersFormSelect = ({ cardFilter, onUpdate }: Props) => {
  const { operator, selections = [] } = cardFilter
  const operatorError = useFilterFormError(!operator)
  const selectionError = useFilterFormError(selections.length === 0)

  const onSelectOperator = (operator: CardFilterSelectType['operator']) => {
    onUpdate({ ...cardFilter, operator })
  }

  const onChange = (next: TypeaheadSelectionType[]) => {
    onUpdate({
      ...cardFilter,
      selections: next.map(({ label, value }) => ({
        label: label || selections.find((s) => s.value === value)?.label || value,
        value,
      })),
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.operator}>
        <InputSelect
          borderColor={operatorError.warn ? 'warning' : undefined}
          colorScheme='muted'
          onSelect={onSelectOperator}
          options={OPTIONS}
          placeholder='Choose operator'
          value={operator}
          wiggle={operatorError.wiggle}
        />
      </div>
      <div className={styles.options}>
        <InputTypeahead
          {...CONFIG[cardFilter.slug]}
          autoFocus
          borderColor={selectionError.warn ? 'warning' : undefined}
          colorScheme='muted'
          multiselect
          onChange={onChange}
          onClear={() => onChange([])}
          value={selections.map((s) => s.value)}
          wiggle={selectionError.wiggle}
        />
      </div>
      <FilterSubmitButton />
    </div>
  )
}
